import { StrokedText } from "@/components/ui/text/stroked-text";
import { useEffect, useState } from "react";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { AddIcon, CheckIcon, PencilIcon, StarIcon, TrashIcon } from "@/icons";
import { Button } from "@/components/ui/buttons/button";
import { Alert } from "@/components/alert/alert";
import { Intent, useSPAContext } from "@/providers/spa-provider";
import { RouteNames } from "@/types/route-names";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { SimpleButton } from "@/components/ui/buttons/simple-button";
import { TeamCard } from "./team-card";
import { updatePokeTeam } from "@/services/poketeam/update-poketeam";
import { deletePokeTeam } from "@/services/poketeam/delete-poketeam";
import { useAuth } from "@/providers/auth-provider";
import { useUser } from "@/providers/user-provider";
import { Pokemon } from "prisma-layer";
import { PokeTeam } from "@/types";
import { DeleteTeamDialog } from "@/components/ui/dialog/delete-team-dialog";
import { UpdateTeamInput } from "types/web-app";

type TeamPokemon = PokeTeam["pokemons"][number];

/**
 * Team Create View - Displays the team create view
 */
export const TeamEditView = () => {
  const {
    setCurrentRoute,
    selectedTeam,
    setSelectedTeam,
    setSelectedPokemons,
    setIntent,
  } = useSPAContext();
  const { getAccessToken } = useAuth();
  const { reloadProfile } = useUser();

  const [teamName, setTeamName] = useState(selectedTeam?.name || "");

  const [teamSlots, setTeamSlots] = useState<(TeamPokemon | null)[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!selectedTeam) return;

    const slots: (TeamPokemon | null)[] = new Array(8).fill(null);
    const pokemons = selectedTeam.pokemons ?? [];
    const order = selectedTeam.order as Record<string, number> | undefined;

    const placedIndices = new Set<number>();
    const remainingPokemons: TeamPokemon[] = [];

    // Place pokemons according to order
    pokemons.forEach((pokemon) => {
      let placed = false;
      if (order) {
        // Find index in order
        const indexStr = Object.keys(order).find(
          (key) => order[key] === pokemon.id,
        );
        if (indexStr) {
          const index = parseInt(indexStr);
          if (!isNaN(index) && index >= 0 && index < 8 && !slots[index]) {
            slots[index] = pokemon;
            placedIndices.add(pokemon.id);
            placed = true;
          }
        }
      }
      if (!placed) {
        remainingPokemons.push(pokemon);
      }
    });

    // Place remaining pokemons in first available slots
    remainingPokemons.forEach((pokemon) => {
      const emptyIndex = slots.findIndex((slot) => slot === null);
      if (emptyIndex !== -1) {
        slots[emptyIndex] = pokemon;
      }
    });

    setTeamSlots(slots);
  }, [selectedTeam]);

  const handleSelectPokemons = () => {
    if (!selectedTeam) {
      Alert("Unexpected error: No team selected");
      setCurrentRoute(RouteNames.TEAM_LIST);
      return;
    }

    const currentPokemons = teamSlots.filter(
      (p): p is TeamPokemon => p !== null,
    );
    setSelectedPokemons(currentPokemons);
    setCurrentRoute(RouteNames.INVENTORY_POKE);
    setIntent(Intent.SELECTING_POKEMONS_FOR_TEAM);
  };

  const handleDeleteTeam = async () => {
    if (isDeleting) return; // Prevent double-clicks

    setIsDeleting(true);
    const accessToken = getAccessToken();
    if (!accessToken) {
      Alert("Authentication error. Please try again");
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
      return;
    }

    if (!selectedTeam) {
      Alert("Unexpected error: No team selected");
      setCurrentRoute(RouteNames.TEAM_LIST);
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
      return;
    }

    try {
      const deleteResult = await deletePokeTeam(accessToken, {
        team_id: selectedTeam.id,
      });

      if (deleteResult.ok) {
        Alert("Team deleted successfully");
        await reloadProfile();
        setCurrentRoute(RouteNames.TEAM_LIST);
      } else {
        Alert("Failed to delete team. Please try again.");
      }
    } catch (error) {
      Alert("Failed to delete team. Please try again.");
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
    }
  };

  const handleSaveAndReturn = async () => {
    if (isSaving) return; // Prevent double-clicks

    setIsSaving(true);
    const accessToken = getAccessToken();
    if (!accessToken) {
      Alert("Authentication error. Please try again");
      setIsSaving(false);
      return;
    }

    if (!selectedTeam) {
      Alert("Unexpected error: No team selected");
      setCurrentRoute(RouteNames.TEAM_LIST);
      setIsSaving(false);
      return;
    }

    const pokemon_ids: number[] = [];
    const order: Record<string, number> = {};

    teamSlots.forEach((pokemon, index) => {
      if (pokemon) {
        pokemon_ids.push(pokemon.id);
        order[index.toString()] = pokemon.id;
      }
    });

    if (pokemon_ids.length === 0) {
      Alert("Team must have at least 1 Pokémon");
      setIsSaving(false);
      return;
    }

    if (teamName.length === 0) {
      Alert("Team must have a name");
      setIsSaving(false);
      return;
    }

    const updateData: UpdateTeamInput = {
      team_id: selectedTeam.id,
      name: teamName,
      order,
      pokemon_ids,
    };

    try {
      const updateResult = await updatePokeTeam(accessToken, updateData);

      // Reload profile to update main team if needed
      await reloadProfile();

      setCurrentRoute(RouteNames.TEAM_LIST);
    } catch (error) {
      Alert("Failed to save team. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!selectedTeam) {
    Alert("Unexpected error: No team selected");
    setCurrentRoute(RouteNames.TEAM_LIST);
    return null;
  }

  return (
    <div className="relative overflow-visible w-full min-h-screen bg-pk-black/80 backdrop-blur-[3px]">
      <div className="flex flex-col w-full  relative pb-12 overflow-visible">
        {/* Header */}
        <div className="relative hidden xs:flex items-center justify-between  w-full px-4 mt-4">
          <div className="absolute h-1/2 w-full left-0 top-1/2 -translate-y-1/2 bg-pk-gray-1"></div>
          <StrokedText strokeWidth={12} fontSize={24} className="z-10">
            MY TEAMS
          </StrokedText>
        </div>

        <div className="flex flex-col px-2 gap-2 mt-2 xs:mt-0">
          {/* Delete Button */}
          <div className="flex items-center justify-end">
            <Button
              variant="red"
              onClick={() => setIsDeleteDialogOpen(true)}
              className="flex items-center gap-2"
            >
              <div className="flex items-center gap-2">
                <TrashIcon
                  width={16}
                  height={16}
                  className="text-white"
                  fill="#fff"
                />
                Delete Team
              </div>
            </Button>
          </div>

          <TeamCard
            team={selectedTeam}
            handleNameChange={setTeamName}
            teamName={teamName}
            handlePokemonChange={handleSelectPokemons}
            slots={teamSlots}
            onSlotsChange={setTeamSlots}
          />
          <div className="flex flex-row justify-center xs:justify-end px-10 xs:px-0">
            <Button
              variant="blue"
              onClick={handleSaveAndReturn}
              disabled={isSaving}
            >
              <span className="text-sm">
                {isSaving ? "Saving..." : "Save and Return"}
              </span>
            </Button>
          </div>
        </div>
      </div>

      <DeleteTeamDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteTeam}
        isLoading={isDeleting}
        teamName={teamName}
      />
    </div>
  );
};
